/**
 * The one place numbers, money, durations and counts become text (DESIGN.md §4.5, D-20): the
 * browser's locale, money in USD, and `—` for a value that is not there. `locale` is for tests.
 */

export const EMPTY = '—';

type Unset = null | undefined;

const cache = new Map<string, Intl.NumberFormat>();
const numberFormat = (locale: string | undefined, options: Intl.NumberFormatOptions = {}) => {
  const key = `${locale ?? ''}${JSON.stringify(options)}`;
  let format = cache.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, options);
    cache.set(key, format);
  }
  return format;
};

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const formatNumber = (value: number | Unset, locale?: string): string =>
  isNumber(value) ? numberFormat(locale).format(value) : EMPTY;

/** `value` is already a percentage (85.3 → 85.3%); one decimal place. */
export const formatPercent = (value: number | Unset, locale?: string): string =>
  isNumber(value)
    ? numberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value / 100)
    : EMPTY;

/** USD. `null` means the amount is unknown, which is not the same as free. */
export const formatMoney = (value: number | Unset, locale?: string): string =>
  isNumber(value)
    ? numberFormat(locale, { style: 'currency', currency: 'USD' }).format(value)
    : EMPTY;

const unitFormat = (
  locale: string | undefined,
  unit: 'second' | 'minute' | 'hour',
  maximumFractionDigits = 1,
) =>
  numberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'long',
    maximumFractionDigits,
  });

/** Milliseconds: under a minute in seconds (to the millisecond below one second), under an hour in minutes, otherwise hours (no days). */
export function formatDuration(ms: unknown, locale?: string): string {
  const value = typeof ms === 'string' && ms.trim() !== '' ? Number(ms) : ms;
  if (!isNumber(value)) return EMPTY;

  const seconds = value / 1000;
  if (Math.abs(seconds) < 1) return unitFormat(locale, 'second', 3).format(seconds);
  if (Math.abs(seconds) < 60) return unitFormat(locale, 'second').format(seconds);
  if (Math.abs(seconds) < 3600) return unitFormat(locale, 'minute').format(seconds / 60);
  return unitFormat(locale, 'hour').format(seconds / 3600);
}

/** "1 request", "3 requests"; pass `other` for an irregular plural. */
export const plural = (count: number, one: string, other = `${one}s`, locale?: string): string =>
  `${formatNumber(count, locale)} ${count === 1 ? one : other}`;

/** "listingTitle", "ai_write", "ai-write" → "Listing title", "Ai write", "Ai write". */
export function humanize(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}
